import { useEffect, useEffectEvent } from 'react';

const MINUTE_MS = 60_000;
const ARROW_STEP_MS = 5 * MINUTE_MS;
/** Keys pressed here belong to that control (typing, the open calendar), not to the bar. */
const OWN_KEYS_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"]';

export interface IReplayKeyRange {
  start: number;
  end: number;
}

interface IUseReplayKeysArgs {
  /** The playable range; null while the day loads (only Esc works then). */
  range: IReplayKeyRange | null;
  atMs: number;
  onClose(): void;
  /** Space: play / pause, given the range start to play a live day from. */
  onToggle(start: number): void;
  /** Arrows: the playhead stepped five minutes, clamped to the range. */
  onSeek(ms: number): void;
}

/**
 * The replay bar's keyboard: Space plays or pauses, ←/→ step five minutes, Esc
 * closes. Subscribes once; the handlers always see the latest props.
 */
export function useReplayKeys({ range, atMs, onClose, onToggle, onSeek }: IUseReplayKeysArgs): void {
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.defaultPrevented || (e.target instanceof Element && e.target.closest(OWN_KEYS_SELECTOR))) return;
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (!range) return;
    if (e.code === 'Space') {
      e.preventDefault();
      onToggle(range.start);
      return;
    }
    const step = { ArrowLeft: -ARROW_STEP_MS, ArrowRight: ARROW_STEP_MS }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    onSeek(Math.min(Math.max(atMs + step, range.start), range.end));
  });

  useEffect(() => {
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
